/**
 * How a client review link is described to the person about to send it.
 *
 * Its own module because the sentence is the point. A link that lapses in two
 * days and a link that lapses in three weeks are both "live", and pasting the
 * first one to a client who is about to go on holiday is how TAP ends up
 * apologising. The panel says which it is before anybody presses Copy.
 */

const DAY_MS = 86_400_000;

/**
 * The life a re-issued link gets, matching the client-link wizard's default
 * (clientLink.ts emptyDraft). Deliberately not configurable at the re-issue
 * point: somebody replacing a lapsed link is solving "the client cannot open
 * it", and a number field in the way of that is a question nobody has an
 * opinion about. The wizard still offers the choice where it belongs.
 */
export const DEFAULT_REVIEW_LINK_DAYS = 30;

export interface LinkLife {
  /** Whole days until it lapses. Negative means it already has. */
  days: number;
  /** What the panel prints next to the link. */
  label: string;
  /** Drives the badge colour, and only ever understates the good case. */
  tone: 'success' | 'warning' | 'danger';
}

/**
 * Days are counted by flooring, so "1 day left" means at least a full day
 * remains rather than at least an hour. Under-reporting is the right direction
 * for a deadline somebody else has to meet.
 *
 * `now` is passed in rather than read, so the wording has a test that does not
 * depend on what day it is run.
 */
export function linkLife(expiresAt: string, now: Date): LinkLife {
  const end = new Date(expiresAt).getTime();
  if (Number.isNaN(end)) {
    // An unparseable timestamp is not a live link anybody should be told to
    // paste. Saying so beats printing "NaN days left".
    return { days: 0, label: 'Expiry unknown', tone: 'warning' };
  }
  const days = Math.floor((end - now.getTime()) / DAY_MS);
  if (days < 0) {
    return { days, label: 'Expired', tone: 'danger' };
  }
  if (days === 0) {
    return { days, label: 'Expires today', tone: 'danger' };
  }
  if (days === 1) {
    return { days, label: 'Expires tomorrow', tone: 'warning' };
  }
  // A week is the threshold where "send it now" stops being urgent advice.
  // Below it the client may reasonably not get to it in time.
  if (days <= 7) {
    return { days, label: `Expires in ${String(days)} days`, tone: 'warning' };
  }
  return { days, label: `Expires in ${String(days)} days`, tone: 'success' };
}

/**
 * Whether re-issuing is the obvious next action rather than an available one.
 *
 * True when there is no live link at all, or when the one there is lapses
 * inside a week. Used to decide emphasis, never to disable anything: somebody
 * may have a good reason to re-issue a link with a month left on it, and the
 * cost of being wrong about that is a button that looks slightly too keen.
 */
export function shouldReissue(life: LinkLife | null): boolean {
  return life === null || life.days <= 7;
}
